import React, { useEffect, useRef, useState } from "react";
import { Avatar, Button, Card, Input, Rate, Tag, Typography } from "antd";
import {
    RobotOutlined,
    SendOutlined,
    UserOutlined,
    EnvironmentOutlined,
    DollarOutlined,
    CheckCircleOutlined,
    LoadingOutlined,
} from "@ant-design/icons";
import { GoogleGenerativeAI, ChatSession } from "@google/generative-ai";
import { get, patch, post } from "../helpers/config";
import { storage } from "../utils/storage";

const { Text, Title } = Typography;
const GEMINI_API_KEY =   process.env.REACT_APP_GEMINI_API_KEY ?? "";
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ?? "";
const UNSUPPORTED_LOCATION_MESSAGE = "We are currently not operating in this country";
const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
const AI_CHAT_STORAGE_PREFIX = "trip_ai_assistant_chat_";
const AI_CHAT_MAX_STORED_MESSAGES = 80;
const AI_CHAT_MAX_HISTORY_MESSAGES = 40;

// ─── Marker Gemini embeds when it detects a booking intent ───────────────────
const BOOKING_MARKER = "%%BOOKING_INTENT%%";

// ─── System instruction for Gemini ───────────────────────────────────────────
const getSystemInstruction = (serviceableCountries: string[]) => `You are a friendly, smart travel assistant for TripAttendynt — a global platform that connects travellers with personal local guides and trip attendants around the world.

TODAY'S DATE: ${new Date().toDateString()}. The current year is ${new Date().getFullYear()}. ALWAYS use this year by default — never ask the user for a year.

IMPORTANT: TripAttendynt currently ONLY operates in the following countries:
${serviceableCountries.length > 0 ? serviceableCountries.join(", ") : "None (currently loading)"}.

If a user requests an attendant or guide in a country NOT in the list above, you MUST politely inform them: "We are currently not operating in [Country]." Do not attempt to gather more details for that trip.

You can have normal conversations: answer travel questions, give destination tips, explain TripAttendynt services, discuss local culture and attractions anywhere in the world (even in non-serviceable countries), chat casually, and help travellers plan their trips. The restriction ONLY applies to booking trip attendants.

When a user wants to BOOK a trip attendant, gather these in order conversationally:
STEP 1 — Trip details (required):
1. City and country
2. Date (use current year automatically)
3. Pickup time (what time attendant should meet them)
4. Duration in hours
5. Pickup location (specific address, hotel, or landmark)
6. Number of people

STEP 2 — Preferences (ask AFTER you have all step 1 details, ask both in one message):
- Gender preference: "Any preference for your attendant's gender? Male, Female, or no preference?"
- Language preference: "Any language preference? (e.g., English, French, Spanish — or no preference)"
- Vehicle type: "Do you need a specific vehicle? (Sedan, SUV, Minivan, Pickup Truck — or no preference)"

HOW TO GATHER INFORMATION SMARTLY:
- Ask one or two questions at a time, never a big list.
- Suggested flow for step 1: location → date + pickup time → duration → pickup address.
- Always ask for number of people before moving to preferences if the traveler has not given it.
- Ask all 3 preference questions together in ONE message after step 1 is complete.
- Keep questions SHORT and DIRECT.
- Be smart about interpreting answers. A bare number like "6" or "4 hours" = duration. A time with am/pm = pickup time.
- Do not assume the number of people. The traveler must provide it explicitly.
- Only ask for what you genuinely don't know.

Once you have ALL of: city/country, date, pickup time, duration, pickup location, AND the user has answered the preference questions (or declined), emit the booking marker:
${BOOKING_MARKER}{"countryId":0,"countryName":"<name>","stateId":0,"cityId":0,"cityName":"<name>","noOfPax":<number>,"scheduleDate":"<ISO e.g. ${new Date().getFullYear()}-07-10T00:00:00>","fromTime":"<HH:MM:00 24h>","toTime":"<fromTime + hours>","hours":<number>,"pickupAddress":"<address>","minRate":<number or null>,"maxRate":<number or null>,"preferredGender":"<Male|Female|null>","preferredLanguages":"<comma-separated language names or null>","preferredVehicle":"<Sedan|SUV|Minivan|Pickup Truck|null>"}

Rules:
- ONLY emit the marker when you have ALL required fields, including number of people, AND preference answers. Otherwise ask naturally.
- Never ask for the year. Use ${new Date().getFullYear()} automatically.
- For casual chat or general questions — respond naturally, no marker.
- The available booking durations are strictly 1, 3, 6, 9, and 12 hours.
- IMPORTANT: All trips must conclude by 11:59 PM on the same day. Calculate the hours remaining from the requested pickup time until 11:59 PM. Only offer and allow durations from [1, 3, 6, 9, 12] that are less than or equal to the remaining hours (e.g., if there are 9 hours left until 11:59 PM, available slots are 1, 3, 6, 9. 12 is NOT available).
- If the user requests a duration that exceeds the remaining hours in the day, inform them of the same-day limit and present ONLY the valid slots that fit.
- Also display a message that they can extend the trip later during the trip if they want (e.g., extend by 1 hour, half an hour, etc.).
- The toTime should be calculated based on the selected duration, and must never exceed 11:59 PM on the same day.
- Conversational text comes BEFORE the marker. Marker + JSON on the same line at the very end.
- Be warm, concise, and confident. Make smart assumptions rather than asking unnecessary questions.
- If the user says "no preference" or similar for any preference, set that field to null in the JSON.
- IMPORTANT: ALWAYS provide your responses in PLAIN TEXT. Never use Markdown formatting like asterisks (e.g., **bold**), hashtags (#), or bullet points with markdown. Use plain conversational language only.`;

// ─── Types ────────────────────────────────────────────────────────────────────

interface BookingIntent {
    countryId: number;
    countryName: string;
    stateId: number;
    cityId: number;
    cityName: string;
    noOfPax: number | null;
    scheduleDate: string;
    fromTime: string;
    toTime: string;
    hours: number;
    pickupAddress: string;
    minRate: number | null;
    maxRate: number | null;
    preferredGender: string | null;
    preferredLanguages: string | null;
    preferredVehicle: string | null;
}

interface AgentCard {
    agentId: string;
    agentName: string;
    agentBio: string;
    agentImageUrl: string;
    agentCityName: string;
    hourlyRate: number;
    agentRating: number;
    scheduleId: string;
    phoneNo: string;
}

interface TripAddResponse {
    tripId: string;
    statusCode: number;
    statusMessage?: string;
    data?: {
        tripId?: string;
        TripId?: string;
        id?: string;
        referenceId?: string;
        scheduleId?: string;
    };
}

interface Message {
    id: string;
    role: "user" | "assistant";
    type: "text" | "intent_card" | "agent_cards" | "success_card" | "confirm_booking_card";
    text?: string;
    intent?: BookingIntent;
    agents?: AgentCard[];
    confirming?: {
        agent: AgentCard;
        totalPrice: number;
        estimatedPlatformFee?: number | null;
    };
    booking?: {
        tripId: string;
        agentName: string;
        scheduleDate: string;
        fromTime: string;
        hours: number;
        totalPrice: number;
        paymentStage?: "platform_fee" | "full_charge";
        platformFeeCharged?: number | null;
        platformFeeTaxAmount?: number | null;
        platformFeeTaxPercentage?: number | null;
        totalPlatformFeeCharged?: number | null;
    };
}

type BookingStep =
    | "idle"
    | "confirm_intent"
    | "searching"
    | "show_results"
    | "confirm_booking"
    | "booking"
    | "booked";

type CardFormState = {
    name: string;
    number: string;
    expiry: string;
    cvc: string;
};

const INITIAL_CARD_FORM: CardFormState = {
    name: "",
    number: "",
    expiry: "",
    cvc: "",
};

// ─── Utilities ────────────────────────────────────────────────────────────────

let _uid = 0;
const uid = () => String(++_uid);
let stripeScriptPromise: Promise<void> | null = null;

function getChatStorageKey(): string {
    const userId = (storage as any).getUserId?.() ?? "";
    const email = (storage as any).getEmailId?.() ?? "";
    const travellerKey = userId || email || "guest";
    return `${AI_CHAT_STORAGE_PREFIX}${travellerKey}`;
}

function getDefaultGreetingMessage(): Message {
    const userName = (storage.getEmailId?.() ?? "").split("@")[0] || "there";
    return {
        id: uid(),
        role: "assistant",
        type: "text",
        text: `Hi ${userName}! 👋 I'm your TripAttendynt assistant.\n\nI can help you book a local trip attendant anywhere in the world, answer travel questions, suggest things to do at your destination, or just chat. What's on your mind?`,
    };
}

function loadPersistedMessages(): Message[] {
    try {
        const raw = window.localStorage.getItem(getChatStorageKey());
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];

        return parsed
            .filter((item) => item && item.role && item.type)
            .map((item) => ({
                ...item,
                id: String(item.id ?? uid()),
                text: item.role === "assistant" && typeof item.text === "string"
                    ? sanitizeAssistantResponseText(item.text)
                    : item.text,
            })) as Message[];
    } catch {
        return [];
    }
}

function persistMessages(messages: Message[]) {
    try {
        const sliced = messages.slice(-AI_CHAT_MAX_STORED_MESSAGES);
        window.localStorage.setItem(getChatStorageKey(), JSON.stringify(sliced));
    } catch {
        // ignore localStorage write errors
    }
}

function syncUidFromMessages(messages: Message[]) {
    const maxId = messages.reduce((max, message) => {
        const numericId = Number(message.id);
        return Number.isFinite(numericId) ? Math.max(max, numericId) : max;
    }, 0);
    if (maxId > _uid) {
        _uid = maxId;
    }
}

function sanitizeAssistantResponseText(value: string): string {
    if (!value) return "";
    // Remove markdown asterisks, hashtags, and other common markdown symbols so responses render as clean plain text.
    return value
        .replace(/\*/g, "")    // Remove asterisks
        .replace(/#/g, "")     // Remove hashtags
        .replace(/_{2,}/g, "") // Remove underscores used for emphasis
        .replace(/`{1,3}/g, "") // Remove backticks
        .trim();
}

function buildGeminiHistoryFromMessages(messages: Message[]) {
    const textMessages = messages
        .filter((message) => message.type === "text" && Boolean(message.text?.trim()))
        .slice(-AI_CHAT_MAX_HISTORY_MESSAGES);

    const history = textMessages.map((message) => ({
        role: message.role === "assistant" ? "model" : "user",
        parts: [{ text: message.text as string }],
    }));

    const firstUserIndex = history.findIndex((item) => item.role === "user");
    if (firstUserIndex === -1) {
        return [];
    }

    return history.slice(firstUserIndex);
}

function isSuccessfulStatusCode(statusCode?: number): boolean {
    return typeof statusCode === "number" && statusCode >= 200 && statusCode < 300;
}

function getFriendlyBookingError(message: string): string {
    if (message.includes("FK_TripPayments_ChargeTypes_ChargeTypeID")) {
        return "platform fee payment could not be processed right now";
    }

    if (message.includes("Invalid platform fee amount")) {
        return "platform fee payment could not be processed right now";
    }

    if (message.toLowerCase().includes("schedule conflict") || message.toLowerCase().includes("overlapping time slot")) {
        return "that time slot is no longer available. Please choose another available time";
    }

    return message;
}

function formatTime(t: string): string {
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}

function formatDate(iso: string): string {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

function genRefId(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
}

function parseMarker(raw: string): { text: string; intent: BookingIntent | null } {
    const idx = raw.indexOf(BOOKING_MARKER);
    if (idx === -1) return { text: sanitizeAssistantResponseText(raw), intent: null };
    const text = sanitizeAssistantResponseText(raw.slice(0, idx));
    try {
        const jsonStr = raw.slice(idx + BOOKING_MARKER.length).trim();
        const intent = JSON.parse(jsonStr) as BookingIntent;
        return { text, intent };
    } catch {
        return { text, intent: null };
    }
}

function normalizeLookupValue(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]/g, "").trim();
}

function getCountryLookupTerms(countryName: string): string[] {
    const trimmed = countryName.trim();
    const normalized = normalizeLookupValue(trimmed);
    const aliases: Record<string, string[]> = {
        us: ["United States", "USA", "US", "United States of America"],
        usa: ["United States", "USA", "US", "United States of America"],
        unitedstates: ["United States", "USA", "US", "United States of America"],
        unitedstatesofamerica: ["United States", "USA", "US", "United States of America"],
        uk: ["United Kingdom", "UK", "Great Britain"],
        unitedkingdom: ["United Kingdom", "UK", "Great Britain"],
        uae: ["United Arab Emirates", "UAE"],
        unitedarabemirates: ["United Arab Emirates", "UAE"],
    };

    return Array.from(new Set([trimmed, ...(aliases[normalized] ?? [])])).filter(Boolean);
}

function getCountryName(country: any): string {
    return country?.countryName ?? country?.CountryName ?? country?.name ?? "";
}

function getCountryCode(country: any): string {
    return country?.countryCode ?? country?.CountryCode ?? country?.code ?? "";
}

function matchesCountry(country: any, countryName: string): boolean {
    const requested = normalizeLookupValue(countryName);
    const countryDisplayName = normalizeLookupValue(getCountryName(country));
    const countryDisplayCode = normalizeLookupValue(getCountryCode(country));

    return countryDisplayName === requested
        || countryDisplayCode === requested
        || countryDisplayName.includes(requested)
        || requested.includes(countryDisplayName)
        || countryDisplayCode.includes(requested)
        || requested.includes(countryDisplayCode);
}

function createUserFacingError(message: string): Error & { isUserFacing: boolean } {
    const error = new Error(message) as Error & { isUserFacing: boolean };
    error.isUserFacing = true;
    return error;
}

function sanitizeCardNumber(value: string): string {
    return value.replace(/\D/g, "").slice(0, 16);
}

function formatCardNumber(value: string): string {
    return sanitizeCardNumber(value).replace(/(.{4})/g, "$1 ").trim();
}

function formatExpiry(value: string): string {
    const digits = value.replace(/\D/g, "").slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

function parseExpiry(value: string): { month: string; year: string } | null {
    const digits = value.replace(/\D/g, "");
    if (digits.length !== 4) return null;

    const month = digits.slice(0, 2);
    const year = digits.slice(2, 4);
    const monthNumber = Number(month);

    if (monthNumber < 1 || monthNumber > 12) return null;

    return { month, year: `20${year}` };
}

async function loadStripeScript(): Promise<void> {
    if (stripeScriptPromise) return stripeScriptPromise;

    stripeScriptPromise = new Promise((resolve, reject) => {
        if (window.Stripe) {
            resolve();
            return;
        }

        const existingScript = document.querySelector('script[src="https://js.stripe.com/v2/"]');
        if (existingScript) {
            existingScript.addEventListener("load", () => resolve(), { once: true });
            existingScript.addEventListener("error", () => reject(new Error("Unable to load Stripe.js.")), { once: true });
            return;
        }

        const script = document.createElement("script");
        script.src = "https://js.stripe.com/v2/";
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Unable to load Stripe.js."));
        document.head.appendChild(script);
    });

    return stripeScriptPromise;
}

async function createStripeToken(cardForm: CardFormState): Promise<string> {
    if (!STRIPE_PUBLISHABLE_KEY) {
        throw new Error("Stripe publishable key is not configured for the web app.");
    }

    const expiry = parseExpiry(cardForm.expiry);
    if (!cardForm.name.trim() || sanitizeCardNumber(cardForm.number).length < 12 || !expiry || cardForm.cvc.trim().length < 3) {
        throw new Error("Please enter a valid debit card before completing payment.");
    }

    await loadStripeScript();

    if (!window.Stripe) {
        throw new Error("Stripe.js did not initialize correctly.");
    }

    const stripeLegacy = window.Stripe;
    if (!stripeLegacy.setPublishableKey || !stripeLegacy.card) {
        throw new Error("Stripe.js legacy card tokenization is not available.");
    }
    const setPublishableKey = stripeLegacy.setPublishableKey;
    const stripeCard = stripeLegacy.card;

    setPublishableKey(STRIPE_PUBLISHABLE_KEY);

    return new Promise((resolve, reject) => {
        stripeCard.createToken(
            {
                name: cardForm.name.trim(),
                number: sanitizeCardNumber(cardForm.number),
                cvc: cardForm.cvc.trim(),
                exp_month: expiry.month,
                exp_year: expiry.year,
            },
            (status, response) => {
                if (status === 200 && response.id) {
                    resolve(response.id);
                    return;
                }

                reject(new Error(response.error?.message || "Unable to tokenize your debit card."));
            },
        );
    });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const TypingIndicator = () => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <Avatar
            size={32}
            style={{ backgroundColor: "#1a1a2e", flexShrink: 0 }}
            icon={<RobotOutlined />}
        />
        <div
            style={{
                background: "#f5f5f5",
                padding: "12px 16px",
                borderRadius: "4px 16px 16px 16px",
                display: "flex",
                gap: 5,
                alignItems: "center",
            }}
        >
            {[0, 1, 2].map((i) => (
                <div
                    key={i}
                    style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: "#6366f1",
                        opacity: 0.6,
                        animation: "bounce 1.2s infinite",
                        animationDelay: `${i * 0.2}s`,
                    }}
                />
            ))}
        </div>
        <style>{`
            @keyframes bounce {
                0%, 80%, 100% { transform: translateY(0); }
                40% { transform: translateY(-6px); }
            }
        `}</style>
    </div>
);

const IntentCard = ({
    intent,
    onConfirm,
    onRetry,
    disabled,
}: {
    intent: BookingIntent;
    onConfirm: () => void;
    onRetry: () => void;
    disabled?: boolean;
}) => {
    const prefRows: [string, string][] = [];
    if (intent.preferredGender) prefRows.push(["Gender Pref", intent.preferredGender]);
    if (intent.preferredLanguages) prefRows.push(["Language Pref", intent.preferredLanguages]);
    if (intent.preferredVehicle) prefRows.push(["Vehicle Pref", intent.preferredVehicle]);

    return (
        <div
            style={{
                background: "linear-gradient(135deg,#667eea,#764ba2)",
                borderRadius: 12,
                padding: "16px 20px",
                color: "#fff",
                maxWidth: 420,
                marginTop: 8,
            }}
        >
            <Text
                style={{
                    color: "rgba(255,255,255,0.75)",
                    fontSize: 13,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    display: "block",
                    marginBottom: 12,
                }}
            >
                Booking Details — confirm to search
            </Text>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
                {[
                    ["Location", `${intent.cityName}, ${intent.countryName}`],
                    ["Date", formatDate(intent.scheduleDate)],
                    ["Time", `${formatTime(intent.fromTime)} – ${formatTime(intent.toTime)}`],
                    ["Duration", `${intent.hours} hr${intent.hours !== 1 ? "s" : ""}`],
                    ["Pickup", intent.pickupAddress],
                    ["Passengers", `${intent.noOfPax} pax`],
                ].map(([label, value]) => (
                    <div key={label}>
                        <Text style={{ color: "rgba(255,255,255,0.55)", fontSize: 13 }}>{label}</Text>
                        <div style={{ color: "#fff", fontWeight: 600, fontSize: 15 }}>{value}</div>
                    </div>
                ))}
                {(intent.minRate !== null || intent.maxRate !== null) && (
                    <div style={{ gridColumn: "1 / -1" }}>
                        <Text style={{ color: "rgba(255,255,255,0.55)", fontSize: 13 }}>Rate Preference</Text>
                        <div style={{ color: "#fff", fontWeight: 600, fontSize: 15 }}>
                            ${intent.minRate ?? "?"} – ${intent.maxRate ?? "?"}/hr
                        </div>
                    </div>
                )}
            </div>
            {prefRows.length > 0 && (
                <>
                    <div style={{ borderTop: "1px solid rgba(255,255,255,0.25)", margin: "12px 0 8px" }} />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px" }}>
                        {prefRows.map(([label, value]) => (
                            <div key={label}>
                                <Text style={{ color: "rgba(255,255,255,0.55)", fontSize: 13 }}>{label}</Text>
                                <div style={{ color: "#fff", fontWeight: 600, fontSize: 15 }}>{value}</div>
                            </div>
                        ))}
                    </div>
                </>
            )}
            {prefRows.length === 0 && (
                <div style={{ marginTop: 8 }}>
                    <Text style={{ color: "rgba(255,255,255,0.55)", fontSize: 13 }}>Preferences</Text>
                    <div style={{ color: "#fff", fontSize: 15 }}>No preferences set</div>
                </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <Button
                    onClick={onConfirm}
                    type="primary"
                    size="small"
                    disabled={disabled}
                    style={{
                        background: disabled ? "rgba(255,255,255,0.2)" : "#fff",
                        color: disabled ? "rgba(255,255,255,0.4)" : "#764ba2",
                        border: "none",
                        fontWeight: 700,
                        fontSize: 15,
                        height: 34
                    }}
                >
                    Yes, find attendants
                </Button>
                <Button
                    onClick={onRetry}
                    size="small"
                    disabled={disabled}
                    style={{
                        background: "transparent",
                        color: disabled ? "rgba(255,255,255,0.3)" : "#fff",
                        border: disabled ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(255,255,255,0.4)",
                        fontSize: 15,
                        height: 34
                    }}
                >
                    Edit details
                </Button>
            </div>
        </div>
    );
};

const AgentCardItem = ({
    agent,
    hours,
    onSelect,
    disabled,
}: {
    agent: AgentCard;
    hours: number;
    onSelect: (a: AgentCard) => void;
    disabled: boolean;
}) => {
    const totalPrice = +(agent.hourlyRate * hours).toFixed(2);
    return (
        <Card
            size="small"
            style={{ width: 320, borderRadius: 12, border: "1px solid #e8e8e8", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
            bodyStyle={{ padding: "14px 16px" }}
        >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <Avatar
                    size={48}
                    src={agent.agentImageUrl || undefined}
                    style={{ backgroundColor: "#6366f1", flexShrink: 0 }}
                >
                    {!agent.agentImageUrl && agent.agentName.charAt(0)}
                </Avatar>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Text strong style={{ fontSize: 16 }}>{agent.agentName}</Text>
                    <div style={{ marginTop: 2 }}>
                        <Rate disabled defaultValue={agent.agentRating || 0} style={{ fontSize: 13 }} />
                        <Text style={{ fontSize: 13, color: "#888", marginLeft: 4 }}>
                            ({agent.agentRating ? agent.agentRating.toFixed(1) : "New"})
                        </Text>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                        <Tag icon={<EnvironmentOutlined />} color="blue" style={{ fontSize: 13 }}>
                            {agent.agentCityName}
                        </Tag>
                        <Tag icon={<DollarOutlined />} color="green" style={{ fontSize: 13 }}>
                            ${agent.hourlyRate}/hr
                        </Tag>
                    </div>
                    {agent.agentBio && (
                        <Text style={{ fontSize: 13, color: "#666", display: "block", marginTop: 6, lineHeight: "1.5" }}>
                            {agent.agentBio.length > 100 ? agent.agentBio.slice(0, 100) + "…" : agent.agentBio}
                        </Text>
                    )}
                </div>
            </div>
            {/* Total price row */}
            <div
                style={{
                    marginTop: 10,
                    background: "#f8f8ff",
                    borderRadius: 8,
                    padding: "8px 12px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderTop: "1px solid #e8e8ef",
                }}
            >
                <Text style={{ fontSize: 14, color: "#666" }}>{hours} hr{hours !== 1 ? "s" : ""} × ${agent.hourlyRate}</Text>
                <Text strong style={{ fontSize: 17, color: "#6366f1" }}>Total: ${totalPrice}</Text>
            </div>
            <Button
                type="primary"
                size="small"
                block
                style={{ marginTop: 10, background: "#6366f1", border: "none", borderRadius: 8, fontSize: 15, height: 36 }}
                onClick={() => onSelect(agent)}
                disabled={disabled}
            >
                Select {agent.agentName.split(" ")[0]}
            </Button>
        </Card>
    );
};

const SuccessCard = ({ booking }: { booking: NonNullable<Message["booking"]> }) => (
    <div
        style={{
            background: "linear-gradient(135deg,#11998e,#38ef7d)",
            borderRadius: 12,
            padding: "16px 20px",
            color: "#fff",
            maxWidth: 360,
            marginTop: 8,
        }}
    >
        {(() => {
            const chargedAmount = booking.paymentStage === "platform_fee"
                ? (booking.platformFeeCharged ?? booking.totalPlatformFeeCharged ?? 0)
                : booking.totalPrice;
            const totalPlatformFeeCharged = booking.totalPlatformFeeCharged ?? chargedAmount;
            return (
                <>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                        <CheckCircleOutlined style={{ fontSize: 22 }} />
                        <Title level={5} style={{ color: "#fff", margin: 0 }}>
                            {booking.paymentStage === "platform_fee" ? "Attendant Selected!" : "Booking Confirmed!"}
                        </Title>
                    </div>
                    <div style={{ fontSize: 15 }}>
                        <div>
                            <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 14 }}>Attendant: </Text>
                            <Text strong style={{ color: "#fff" }}>{booking.agentName}</Text>
                        </div>
                        <div style={{ marginTop: 4 }}>
                            <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 14 }}>Date: </Text>
                            <Text strong style={{ color: "#fff" }}>{formatDate(booking.scheduleDate)}</Text>
                        </div>
                        <div style={{ marginTop: 4 }}>
                            <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 14 }}>Time: </Text>
                            <Text strong style={{ color: "#fff" }}>{formatTime(booking.fromTime)} · {booking.hours} hrs</Text>
                        </div>
                        <div style={{ marginTop: 8, borderTop: "1px solid rgba(255,255,255,0.3)", paddingTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 15 }}>
                                {booking.paymentStage === "platform_fee" ? "Platform Fee" : "Total Charged"}
                            </Text>
                            <Text strong style={{ color: "#fff", fontSize: 18 }}>${chargedAmount.toFixed(2)}</Text>
                        </div>
                        {booking.paymentStage === "platform_fee" && (
                            <>
                                {booking.platformFeeTaxAmount !== null && booking.platformFeeTaxAmount !== undefined && (
                                    <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 14 }}>
                                            Tax{booking.platformFeeTaxPercentage !== null && booking.platformFeeTaxPercentage !== undefined
                                                ? ` (${booking.platformFeeTaxPercentage.toFixed(2)}%)`
                                                : ""}
                                        </Text>
                                        <Text style={{ color: "#fff", fontSize: 15 }}>${booking.platformFeeTaxAmount.toFixed(2)}</Text>
                                    </div>
                                )}
                                <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 14 }}>Total Platform Fee Charged</Text>
                                    <Text style={{ color: "#fff", fontSize: 15 }}>${totalPlatformFeeCharged.toFixed(2)}</Text>
                                </div>
                                <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 14 }}>Estimated Trip Amount</Text>
                                    <Text style={{ color: "#fff", fontSize: 15 }}>${booking.totalPrice.toFixed(2)}</Text>
                                </div>
                            </>
                        )}
                    </div>
                    <Text style={{ color: "rgba(255,255,255,0.8)", fontSize: 13, display: "block", marginTop: 10, lineHeight: 1.5 }}>
                        {booking.paymentStage === "platform_fee"
                            ? "Platform fee is charged first. The remaining trip charge is processed after attendant acceptance."
                            : "View full details in My Trips."}
                    </Text>
                </>
            );
        })()}
    </div>
);

const ConfirmBookingCard = ({
    confirming,
    intent,
    onConfirm,
    onBack,
    disabled,
    savedCardLast4,
    useNewCard,
    onUseSavedCard,
    onUseNewCard,
    cardForm,
    onCardChange,
    paymentMessage,
    paymentError,
    isStripeConfigured,
    hasStripeCustomer,
}: {
    confirming: NonNullable<Message["confirming"]>;
    intent: BookingIntent;
    onConfirm: () => void;
    onBack: () => void;
    disabled: boolean;
    savedCardLast4: string | null;
    useNewCard: boolean;
    onUseSavedCard: () => void;
    onUseNewCard: () => void;
    cardForm: CardFormState;
    onCardChange: (field: keyof CardFormState, value: string) => void;
    paymentMessage: string | null;
    paymentError: string | null;
    isStripeConfigured: boolean;
    hasStripeCustomer: boolean;
}) => {
    const { agent, totalPrice, estimatedPlatformFee } = confirming;
    return (
        <div
            style={{
                background: "linear-gradient(135deg,#1a1a2e,#16213e)",
                borderRadius: 12,
                padding: "16px 20px",
                color: "#fff",
                maxWidth: 380,
                marginTop: 8,
            }}
        >
            <Text style={{ color: "rgba(255,255,255,0.6)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 12 }}>
                Confirm Your Booking
            </Text>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <Avatar size={44} src={agent.agentImageUrl || undefined} style={{ backgroundColor: "#6366f1", flexShrink: 0 }}>
                    {!agent.agentImageUrl && agent.agentName.charAt(0)}
                </Avatar>
                <div>
                    <Text strong style={{ color: "#fff", fontSize: 17, display: "block" }}>{agent.agentName}</Text>
                    <Text style={{ color: "rgba(255,255,255,0.5)", fontSize: 14 }}>{agent.agentCityName}</Text>
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px", marginBottom: 14 }}>
                {[
                    ["Date", formatDate(intent.scheduleDate)],
                    ["Time", formatTime(intent.fromTime)],
                    ["Duration", `${intent.hours} hr${intent.hours !== 1 ? "s" : ""}`],
                    ["Pickup", intent.pickupAddress],
                ].map(([label, value]) => (
                    <div key={label}>
                        <Text style={{ color: "rgba(255,255,255,0.45)", fontSize: 13 }}>{label}</Text>
                        <div style={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>{value}</div>
                    </div>
                ))}
            </div>
            <div
                style={{
                    background: "rgba(99,102,241,0.25)",
                    borderRadius: 8,
                    padding: "10px 14px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 14,
                    border: "1px solid rgba(99,102,241,0.4)",
                }}
            >
                <div>
                    <Text style={{ color: "rgba(255,255,255,0.55)", fontSize: 13 }}>
                        {intent.hours} hrs × ${agent.hourlyRate}/hr
                    </Text>
                </div>
                <div>
                    <Text style={{ color: "rgba(255,255,255,0.55)", fontSize: 13 }}>Total Price</Text>
                    <Text strong style={{ color: "#a78bfa", fontSize: 22, display: "block", lineHeight: 1 }}>
                        ${totalPrice}
                    </Text>
                </div>
            </div>
            <div
                style={{
                    background: "rgba(255,255,255,0.08)",
                    borderRadius: 10,
                    padding: "12px 14px",
                    marginBottom: 14,
                    border: "1px solid rgba(255,255,255,0.12)",
                }}
            >
                <Text style={{ color: "rgba(255,255,255,0.78)", fontSize: 13, display: "block", marginBottom: 8 }}>
                    Platform fee payment
                </Text>
                {estimatedPlatformFee !== null && estimatedPlatformFee !== undefined && (
                    <div
                        style={{
                            background: "rgba(99,102,241,0.2)",
                            borderRadius: 8,
                            padding: "8px 10px",
                            marginBottom: 8,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            border: "1px solid rgba(99,102,241,0.35)",
                        }}
                    >
                        <Text style={{ color: "rgba(255,255,255,0.78)", fontSize: 13 }}>
                            Estimated Platform Fee
                        </Text>
                        <Text strong style={{ color: "#fff", fontSize: 16 }}>
                            ${estimatedPlatformFee.toFixed(2)}
                        </Text>
                    </div>
                )}
                {paymentMessage && (
                    <Text style={{ color: "rgba(255,255,255,0.9)", fontSize: 13, display: "block", lineHeight: 1.5, marginBottom: 8 }}>
                        {paymentMessage}
                    </Text>
                )}
                {savedCardLast4 && !useNewCard && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <Text style={{ color: "#fff", fontSize: 14 }}>
                            Saved debit card ending in {savedCardLast4}
                        </Text>
                        <Button size="small" onClick={onUseNewCard} style={{ fontSize: 13 }}>
                            Use another card
                        </Button>
                    </div>
                )}
                {useNewCard && (
                    <>
                        {savedCardLast4 && (
                            <Button size="small" onClick={onUseSavedCard} style={{ marginBottom: 10, fontSize: 13 }}>
                                Use saved card ending in {savedCardLast4}
                            </Button>
                        )}
                        {!isStripeConfigured ? (
                            <Text style={{ color: "#ffd6d6", fontSize: 13, display: "block", lineHeight: 1.5 }}>
                                Stripe debit card setup is not configured for the web app yet.
                            </Text>
                        ) : !hasStripeCustomer ? (
                            <Text style={{ color: "#ffd6d6", fontSize: 13, display: "block", lineHeight: 1.5 }}>
                                We could not prepare your Stripe customer profile. Please try again.
                            </Text>
                        ) : (
                            <div style={{ display: "grid", gap: 10 }}>
                                <Input
                                    placeholder="Name on card"
                                    value={cardForm.name}
                                    onChange={(event) => onCardChange("name", event.target.value)}
                                    disabled={disabled}
                                    style={{ borderRadius: 8, fontSize: 14 }}
                                />
                                <Input
                                    placeholder="Card number"
                                    value={cardForm.number}
                                    onChange={(event) => onCardChange("number", formatCardNumber(event.target.value))}
                                    disabled={disabled}
                                    style={{ borderRadius: 8, fontSize: 14 }}
                                />
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                                    <Input
                                        placeholder="MM/YY"
                                        value={cardForm.expiry}
                                        onChange={(event) => onCardChange("expiry", formatExpiry(event.target.value))}
                                        disabled={disabled}
                                        style={{ borderRadius: 8, fontSize: 14 }}
                                    />
                                    <Input
                                        placeholder="CVC"
                                        value={cardForm.cvc}
                                        onChange={(event) => onCardChange("cvc", event.target.value.replace(/\D/g, "").slice(0, 4))}
                                        disabled={disabled}
                                        style={{ borderRadius: 8, fontSize: 14 }}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
                {paymentError && (
                    <Text style={{ color: "#ffd6d6", fontSize: 13, display: "block", marginTop: 10, lineHeight: 1.5 }}>
                        {paymentError}
                    </Text>
                )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
                <Button
                    type="primary"
                    size="small"
                    style={{ background: "#6366f1", border: "none", fontWeight: 700, flex: 1, fontSize: 15, height: 36 }}
                    onClick={onConfirm}
                    disabled={disabled}
                >
                    ✓ Confirm &amp; Book
                </Button>
                <Button
                    size="small"
                    style={{ background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.4)", fontSize: 15, height: 36 }}
                    onClick={onBack}
                    disabled={disabled}
                >
                    Back
                </Button>
            </div>
        </div>
    );
};

// ─── Message bubble ───────────────────────────────────────────────────────────

const MessageBubble = ({
    msg,
    isLastAssistant,
    isTyping,
    onConfirmIntent,
    onRetryIntent,
    onSelectAgent,
    onConfirmBooking,
    onBackToResults,
    bookingStep,
    pendingIntent,
    savedCardLast4,
    useNewCard,
    onUseSavedCard,
    onUseNewCard,
    cardForm,
    onCardChange,
    paymentMessage,
    paymentError,
    isStripeConfigured,
    hasStripeCustomer,
    disabledIntent,
}: {
    msg: Message;
    isLastAssistant: boolean;
    isTyping: boolean;
    onConfirmIntent: () => void;
    onRetryIntent: () => void;
    onSelectAgent: (a: AgentCard) => void;
    onConfirmBooking: () => void;
    onBackToResults: () => void;
    bookingStep: BookingStep;
    pendingIntent: BookingIntent | null;
    savedCardLast4: string | null;
    useNewCard: boolean;
    onUseSavedCard: () => void;
    onUseNewCard: () => void;
    cardForm: CardFormState;
    onCardChange: (field: keyof CardFormState, value: string) => void;
    paymentMessage: string | null;
    paymentError: string | null;
    isStripeConfigured: boolean;
    hasStripeCustomer: boolean;
    disabledIntent?: boolean;
}) => (
    <div
        style={{
            display: "flex",
            flexDirection: msg.role === "user" ? "row-reverse" : "row",
            alignItems: "flex-start",
            gap: 10,
        }}
    >
        <Avatar
            size={32}
            style={{ backgroundColor: msg.role === "user" ? "#6366f1" : "#1a1a2e", flexShrink: 0 }}
            icon={msg.role === "user" ? <UserOutlined /> : <RobotOutlined />}
        />
        <div style={{ maxWidth: "78%" }}>
            {msg.type === "text" && msg.text && (
                <div
                    style={{
                        background: msg.role === "user" ? "#6366f1" : "#f5f5f5",
                        color: msg.role === "user" ? "#fff" : "#333",
                        padding: "14px 18px",
                        borderRadius: msg.role === "user" ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
                        fontSize: 17,
                        lineHeight: "1.7",
                        whiteSpace: "pre-line",
                    }}
                >
                    {msg.role === "assistant" ? sanitizeAssistantResponseText(msg.text) : msg.text}
                    {isLastAssistant && isTyping && (
                        <LoadingOutlined style={{ marginLeft: 8, fontSize: 12, color: "#6366f1" }} spin />
                    )}
                </div>
            )}
            {msg.type === "intent_card" && msg.intent && (
                <div>
                    {msg.text && (
                        <div style={{ background: "#f5f5f5", color: "#333", padding: "14px 18px", borderRadius: "4px 16px 16px 16px", fontSize: 17, lineHeight: "1.7", whiteSpace: "pre-line", marginBottom: 6 }}>
                            {sanitizeAssistantResponseText(msg.text)}
                        </div>
                    )}
                    <IntentCard intent={msg.intent} onConfirm={onConfirmIntent} onRetry={onRetryIntent} disabled={disabledIntent} />
                </div>
            )}
            {msg.type === "agent_cards" && msg.agents && (
                <div>
                    {msg.text && (
                        <div style={{ background: "#f5f5f5", color: "#333", padding: "14px 18px", borderRadius: "4px 16px 16px 16px", fontSize: 17, lineHeight: "1.7", marginBottom: 10, whiteSpace: "pre-line" }}>
                            {sanitizeAssistantResponseText(msg.text)}
                        </div>
                    )}
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {msg.agents.map((agent) => (
                            <AgentCardItem
                                key={agent.agentId}
                                agent={agent}
                                hours={pendingIntent?.hours ?? 1}
                                onSelect={onSelectAgent}
                                disabled={bookingStep === "confirm_booking" || bookingStep === "booking" || bookingStep === "booked"}
                            />
                        ))}
                    </div>
                </div>
            )}
            {msg.type === "confirm_booking_card" && msg.confirming && pendingIntent && (
                <ConfirmBookingCard
                    confirming={msg.confirming}
                    intent={pendingIntent}
                    onConfirm={onConfirmBooking}
                    onBack={onBackToResults}
                    disabled={bookingStep === "booking" || bookingStep === "booked"}
                    savedCardLast4={savedCardLast4}
                    useNewCard={useNewCard}
                    onUseSavedCard={onUseSavedCard}
                    onUseNewCard={onUseNewCard}
                    cardForm={cardForm}
                    onCardChange={onCardChange}
                    paymentMessage={paymentMessage}
                    paymentError={paymentError}
                    isStripeConfigured={isStripeConfigured}
                    hasStripeCustomer={hasStripeCustomer}
                />
            )}
            {msg.type === "success_card" && msg.booking && <SuccessCard booking={msg.booking} />}
        </div>
    </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const AiTripAgent: React.FC = () => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [bookingStep, setBookingStep] = useState<BookingStep>("idle");
    const [pendingIntent, setPendingIntent] = useState<BookingIntent | null>(null);
    const [serviceableCountries, setServiceableCountries] = useState<string[]>([]);
    const [currentTripId, setCurrentTripId] = useState<string | null>(null);
    const [currentReferenceId, setCurrentReferenceId] = useState<string | null>(null);
    const [selectedAgent, setSelectedAgent] = useState<AgentCard | null>(null);
    const [resolvedLocation, setResolvedLocation] = useState<{ countryId: number; stateId: number; cityId: number } | null>(null);
    const [resolvedLanguageIds, setResolvedLanguageIds] = useState<string | null>(null);
    const [stripeCustomerId, setStripeCustomerId] = useState<string | null>(null);
    const [savedCardLast4, setSavedCardLast4] = useState<string | null>(null);
    const [useNewCard, setUseNewCard] = useState(false);
    const [cardForm, setCardForm] = useState<CardFormState>(INITIAL_CARD_FORM);
    const [paymentMessage, setPaymentMessage] = useState<string | null>(null);
    const [paymentError, setPaymentError] = useState<string | null>(null);
    const chatSessionRef = useRef<ChatSession | null>(null);
    const bottomRef = useRef<HTMLDivElement>(null);

    // Boot: initialise Gemini chat session and restore persisted conversation if available
    useEffect(() => {
        if (!GEMINI_API_KEY) return;

        const initChat = async () => {
            let countries: string[] = [];
            try {
                const res = await get("country/list?IsServiceableAttendant=true");
                const countryData = res.data?.countries ?? res.data ?? [];
                countries = countryData.map((c: any) => c.countryName || c.CountryName).filter(Boolean);
                setServiceableCountries(countries);
            } catch (err) {
                console.error("Failed to fetch serviceable countries", err);
            }

            const restoredMessages = loadPersistedMessages();
            const initialMessages = restoredMessages.length > 0 ? restoredMessages : [getDefaultGreetingMessage()];
            syncUidFromMessages(initialMessages);

            const genAI = new GoogleGenerativeAI(GEMINI_API_KEY);
            const model = genAI.getGenerativeModel({
                model: "gemini-2.5-flash",
                systemInstruction: getSystemInstruction(countries),
            });
            chatSessionRef.current = model.startChat({ history: buildGeminiHistoryFromMessages(initialMessages) });
            setMessages(initialMessages);
        };

        initChat();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (messages.length === 0) return;
        persistMessages(messages);
    }, [messages]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isTyping]);

    const addMessage = (msg: Omit<Message, "id">) =>
        setMessages((prev) => [...prev, { ...msg, id: uid() }]);

    const resetPaymentState = () => {
        setStripeCustomerId(null);
        setSavedCardLast4(null);
        setUseNewCard(false);
        setCardForm(INITIAL_CARD_FORM);
        setPaymentMessage(null);
        setPaymentError(null);
    };

    const getEstimatedPlatformFee = async (countryId: number, scheduleDate: string, fromTime: string): Promise<number | null> => {
        try {
            const settingRes = await get(`generalsetting?settingCode=PLATFORM_FEE&countryId=${countryId}`);
            if (settingRes?.statusCode !== 200) return null;

            const rawSetting = Array.isArray(settingRes.data) ? settingRes.data[0] : settingRes.data;
            const baseFee = Number(rawSetting?.settingValue);
            if (!Number.isFinite(baseFee) || baseFee <= 0) return null;

            const datePart = scheduleDate?.split("T")?.[0];
            if (!datePart) return +baseFee.toFixed(2);

            const timePart = fromTime ? fromTime.slice(0, 5) : "00:00";
            const arrival = new Date(`${datePart}T${timePart}:00`);
            if (Number.isNaN(arrival.getTime())) return +baseFee.toFixed(2);

            const now = new Date();
            const effectiveArrival = arrival < now ? now : arrival;
            const plus24Hours = new Date(now.getTime() + (24 * 60 * 60 * 1000));
            const shouldDouble = effectiveArrival >= now && effectiveArrival <= plus24Hours;

            return +(shouldDouble ? baseFee * 2 : baseFee).toFixed(2);
        } catch {
            return null;
        }
    };

    const handleSend = async () => {
        const text = input.trim();
        if (!text || isTyping || bookingStep === "booking" || bookingStep === "searching") return;

        setInput("");
        addMessage({ role: "user", type: "text", text });

        if (!GEMINI_API_KEY) {
            addMessage({
                role: "assistant",
                type: "text",
                text: "⚠️ Gemini API key not configured. Add REACT_APP_GEMINI_API_KEY to .env and restart.",
            });
            return;
        }

        if (!chatSessionRef.current) return;

        setIsTyping(true);

        try {
            const result = await chatSessionRef.current.sendMessage(text);
            const raw = result.response.text();
            const { text: assistantText, intent } = parseMarker(raw);

            setIsTyping(false);

            if (intent) {
                setPendingIntent(intent);
                setBookingStep("confirm_intent");
                addMessage({ role: "assistant", type: "intent_card", text: assistantText || undefined, intent });
            } else {
                addMessage({ role: "assistant", type: "text", text: assistantText });
            }
        } catch (err: any) {
            setIsTyping(false);
            addMessage({
                role: "assistant",
                type: "text",
                text: `Sorry, something went wrong: ${err.message}`,
            });
        }
    };

    const handleConfirmIntent = async () => {
        if (!pendingIntent) return;

        if (!pendingIntent.noOfPax || pendingIntent.noOfPax <= 0) {
            setBookingStep("idle");
            addMessage({
                role: "assistant",
                type: "text",
                text: "How many people will be traveling?",
            });
            return;
        }

        setBookingStep("searching");

        const referenceId = genRefId();
        setCurrentReferenceId(referenceId);

        addMessage({ role: "assistant", type: "text", text: "Searching for available attendants that match your preferences and schedule..." });

        try {
            let resolvedCountryId = pendingIntent.countryId;
            let resolvedStateId = pendingIntent.stateId;
            let resolvedCityId = pendingIntent.cityId;

            if (!resolvedCountryId || resolvedCountryId === 0) {
                const countryLookupTerms = getCountryLookupTerms(pendingIntent.countryName);
                const serviceableCountryResults = await Promise.all(
                    countryLookupTerms.map((term) =>
                        get(`country/list?searchFilter=${encodeURIComponent(term)}&IsServiceableAttendant=true`)
                    )
                );

                const serviceableCountries = serviceableCountryResults.flatMap((response) => response.data?.countries ?? response.data ?? []);
                const serviceableCountryMatch = serviceableCountries.find((country: any) => matchesCountry(country, pendingIntent.countryName));

                if (serviceableCountryMatch) {
                    resolvedCountryId = serviceableCountryMatch.countryId ?? serviceableCountryMatch.id ?? 0;
                } else {
                    const allCountryResults = await Promise.all(
                        countryLookupTerms.map((term) =>
                            get(`country/list?searchFilter=${encodeURIComponent(term)}`)
                        )
                    );

                    const allCountries = allCountryResults.flatMap((response) => response.data?.countries ?? response.data ?? []);
                    const countryExists = allCountries.some((country: any) => matchesCountry(country, pendingIntent.countryName));

                    if (countryExists || allCountries.length === 0) {
                        throw createUserFacingError(`We are currently not operating in ${pendingIntent.countryName}`);
                    }

                    if (!countryExists) {
                        throw createUserFacingError(`We are currently not operating in ${pendingIntent.countryName}`);
                    }
                }
            }

            if ((!resolvedStateId || resolvedStateId === 0) && resolvedCountryId) {
                const stateRes = await get(`state/list?countryId=${resolvedCountryId}`);
                const states: any[] = stateRes.data?.states ?? stateRes.data ?? [];
                const stateMatch = pendingIntent.stateId === 0
                    ? states[0]
                    : states.find((s: any) =>
                        (s.stateName ?? s.name ?? "").toLowerCase().includes(pendingIntent.countryName.toLowerCase())
                    ) ?? states[0];
                if (stateMatch) resolvedStateId = stateMatch.stateId ?? stateMatch.id ?? 0;
            }

            if ((!resolvedCityId || resolvedCityId === 0) && resolvedCountryId) {
                const cityRes = await get(`city/list?countryId=${resolvedCountryId}&searchFilter=${encodeURIComponent(pendingIntent.cityName)}&IsServiceableAttendant=true`);
                const cities: any[] = cityRes.data?.cities ?? cityRes.data ?? [];
                const cityMatch = cities.find((c: any) =>
                    (c.cityName ?? c.name ?? "").toLowerCase().includes(pendingIntent.cityName.toLowerCase())
                ) ?? cities[0];
                if (cityMatch) {
                    resolvedCityId = cityMatch.cityId ?? cityMatch.id ?? 0;
                    if (!resolvedStateId || resolvedStateId === 0) {
                        resolvedStateId = cityMatch.stateId ?? resolvedStateId;
                    }
                }
            }

            if (!resolvedCountryId || !resolvedStateId || !resolvedCityId) {
                throw new Error(`Sorry, I couldn't find "${pendingIntent.cityName}, ${pendingIntent.countryName}" in our system. Please try another city or check the spelling.`);
            }

            setResolvedLocation({ countryId: resolvedCountryId, stateId: resolvedStateId, cityId: resolvedCityId });

            let resolvedLangIds: string | null = null;
            if (pendingIntent.preferredLanguages) {
                const langRes = await get("language/list");
                const allLangs: any[] = langRes.data?.languages ?? langRes.data ?? [];
                const requestedNames = pendingIntent.preferredLanguages.split(",").map((s) => s.trim().toLowerCase());
                const matchedIds = allLangs
                    .filter((l: any) => requestedNames.some((n) => (l.languageName ?? l.name ?? "").toLowerCase().includes(n)))
                    .map((l: any) => String(l.languageId ?? l.id));
                if (matchedIds.length > 0) resolvedLangIds = matchedIds.join(",");
            }
            setResolvedLanguageIds(resolvedLangIds);

            // ── 1. Create the trip now that user has confirmed ──────────────────
            const travellerId = (storage as any).getUserId?.() ?? null;
            let tripId: string | null = null;
            let finalReferenceId: string = referenceId;

            try {
                const tripRes: TripAddResponse = await post("trip/add", {
                    travellerId,
                    tripId: null,
                    countryId: resolvedCountryId,
                    stateId: resolvedStateId,
                    cityId: resolvedCityId,
                    noOfPax: pendingIntent.noOfPax,
                    scheduleId: null,
                    referenceId,
                    pickupAddress: pendingIntent.pickupAddress,
                    scheduleDate: pendingIntent.scheduleDate,
                    hours: pendingIntent.hours,
                    fromTime: pendingIntent.fromTime,
                    toTime: pendingIntent.toTime,
                });
                
                if (isSuccessfulStatusCode(tripRes.statusCode)) {
                    tripId = tripRes.data?.tripId ?? tripRes.data?.TripId ?? tripRes.data?.id ?? null;
                    const schId = tripRes.data?.scheduleId ?? null;
                    finalReferenceId = tripRes.data?.referenceId ?? referenceId;
                    setCurrentTripId(tripId);

                    // ── 1.5 Sync traveler preferences ───────────────────────
                    if (tripId) {
                        const tripPreferences = [
                            {
                                preferenceName: "Gender",
                                preferenceValue: pendingIntent.preferredGender ?? ""
                            },
                            {
                                preferenceName: "Languages",
                                preferenceValue: resolvedLangIds ?? ""
                            },
                            {
                                preferenceName: "Transportation",
                                preferenceValue: "true"
                            }
                        ];
                        await post(`trip/preferences?tripId=${tripId}`, { tripPreferences }).catch(() => { });
                    }
                } else {
                    throw new Error(tripRes.statusMessage || "Failed to create trip.");
                }
            } catch (tripErr: any) {
                console.error("Failed to pre-create trip:", tripErr);
                throw new Error(tripErr?.message || "Unable to create your trip record. Please try again.");
            }

            if (!tripId) {
                throw new Error("Unable to create your trip record. Please check the details and try again.");
            }

            // ── 2. Search available attendants ──────────────────
            const agentsRes = await get(`agent/availabilities?tripId=${tripId}&referenceId=${finalReferenceId}`);

            if (agentsRes.statusCode !== 200) throw new Error("Unable to find available attendants. Please try again.");

            let agents: AgentCard[] = agentsRes.data?.agentAvailability ?? [];
            if (pendingIntent.minRate !== null || pendingIntent.maxRate !== null) {
                agents = agents.filter((a) => {
                    if (pendingIntent.minRate !== null && a.hourlyRate < pendingIntent.minRate) return false;
                    if (pendingIntent.maxRate !== null && a.hourlyRate > pendingIntent.maxRate) return false;
                    return true;
                });
            }

            setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === "assistant" && last.type === "text") {
                    updated[updated.length - 1] = {
                        ...last,
                        type: agents.length > 0 ? "agent_cards" : "text",
                        agents: agents.length > 0 ? agents : undefined,
                        text: agents.length > 0
                            ? `Found ${agents.length} available attendant${agents.length !== 1 ? "s" : ""} matching your preferences and available on ${formatDate(pendingIntent.scheduleDate)}. Pick one to see the total price:`
                            : `No attendants available in ${pendingIntent.cityName} on ${formatDate(pendingIntent.scheduleDate)} matching your preferences. Want to try a different date or adjust your preferences?`,
                    };
                }
                return updated;
            });

            setBookingStep(agents.length > 0 ? "show_results" : "idle");
        } catch (err: any) {
            setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === "assistant") {
                    updated[updated.length - 1] = {
                        ...last,
                        text: err?.isUserFacing ? err.message : `Error: ${err.message}. Please try again.`,
                    };
                }
                return updated;
            });
            setBookingStep("idle");
        }
    };

    const handleRetryIntent = () => {
        setBookingStep("idle");
        setPendingIntent(null);
        setResolvedLocation(null);
        setResolvedLanguageIds(null);
        resetPaymentState();
        addMessage({
            role: "assistant",
            type: "text",
            text: "No problem! Tell me what you'd like to change - different date, time, location, or anything else.",
        });
    };

    const handleSelectAgent = async (agent: AgentCard) => {
        if (!pendingIntent) return;
        setSelectedAgent(agent);
        setBookingStep("confirm_booking");
        setPaymentError(null);
        setCardForm(INITIAL_CARD_FORM);
        const totalPrice = +(agent.hourlyRate * pendingIntent.hours).toFixed(2);
        let estimatedPlatformFee: number | null = null;

        if (resolvedLocation?.countryId) {
            estimatedPlatformFee = await getEstimatedPlatformFee(
                resolvedLocation.countryId,
                pendingIntent.scheduleDate,
                pendingIntent.fromTime,
            );
        }

        try {
            const travellerId = (storage as any).getUserId?.() ?? null;
            if (!travellerId) {
                throw new Error("Traveller account not found. Please sign in again.");
            }

            const stripeCustomerRes = await post(`trip-payment/stripe/customer/create?travellerId=${travellerId}`, {});
            if (!isSuccessfulStatusCode(stripeCustomerRes.statusCode)) {
                throw new Error(stripeCustomerRes.statusMessage || "Unable to prepare Stripe payment.");
            }

            const customerId = stripeCustomerRes.data?.customerId ? String(stripeCustomerRes.data.customerId) : null;
            const last4 = stripeCustomerRes.data?.last4 ? String(stripeCustomerRes.data.last4) : null;

            setStripeCustomerId(customerId);
            setSavedCardLast4(last4);
            setUseNewCard(!last4);
            setPaymentMessage(
                last4
                    ? `Platform fee${estimatedPlatformFee ? ` (${estimatedPlatformFee.toFixed(2)})` : ""} will be charged to your saved debit card ending in ${last4}.`
                    : STRIPE_PUBLISHABLE_KEY
                        ? `Add a debit card to pay the platform fee${estimatedPlatformFee ? ` (${estimatedPlatformFee.toFixed(2)})` : ""} and complete the booking.`
                        : "Stripe debit card setup is not configured for the web app yet.",
            );
        } catch (err: any) {
            resetPaymentState();
            setUseNewCard(true);
            setPaymentMessage(err.message || "Unable to prepare payment right now.");
        }

        addMessage({
            role: "assistant",
            type: "confirm_booking_card",
            confirming: { agent, totalPrice, estimatedPlatformFee },
        });
    };

    const handleBackToResults = () => {
        setBookingStep("show_results");
        setSelectedAgent(null);
        resetPaymentState();
        setMessages((prev) => prev.filter((m) => m.type !== "confirm_booking_card"));
    };

    const handleCardFieldChange = (field: keyof CardFormState, value: string) => {
        setCardForm((prev) => ({ ...prev, [field]: value }));
        setPaymentError(null);
    };

    const handleUseSavedCard = () => {
        setUseNewCard(false);
        setPaymentError(null);
    };

    const handleUseNewCard = () => {
        setUseNewCard(true);
        setPaymentError(null);
    };

    // Step 2: create trip + post preferences + select agent (platform fee first)
    const handleFinalBook = async () => {
        if (!currentReferenceId || !pendingIntent || !selectedAgent || !resolvedLocation) return;
        const agent = selectedAgent;
        const totalPrice = +(agent.hourlyRate * pendingIntent.hours).toFixed(2);
        const travellerId = (storage as any).getUserId?.() ?? null;

        if (!stripeCustomerId) {
            setPaymentError("Please add a debit card before completing the booking.");
            return;
        }

        let tokenId = "";
        if (useNewCard) {
            try {
                tokenId = await createStripeToken(cardForm);
            } catch (err: any) {
                setPaymentError(err.message || "Please add a valid debit card.");
                return;
            }
        }

        setBookingStep("booking");
        setPaymentError(null);
        let createdTripId: string | null = null;

        // Replace confirm card with a "booking…" text message
        setMessages((prev) => prev.map((m) =>
            m.type === "confirm_booking_card"
                ? { ...m, type: "text" as const, text: `Creating your trip, selecting ${agent.agentName}, and charging the platform fee…`, confirming: undefined }
                : m
        ));

        try {
            // ── 1. Use existing trip or create it now ──────────────────
            let tripId: string = "";
            
            if (currentTripId) {
                tripId = currentTripId;
            } else {
                const tripRes: TripAddResponse = await post("trip/add", {
                    travellerId,
                    tripId: null,
                    countryId: resolvedLocation.countryId,
                    stateId: resolvedLocation.stateId,
                    cityId: resolvedLocation.cityId,
                    noOfPax: pendingIntent.noOfPax,
                    scheduleId: null,
                    referenceId: currentReferenceId,
                    pickupAddress: pendingIntent.pickupAddress,
                    scheduleDate: pendingIntent.scheduleDate,
                    hours: pendingIntent.hours,
                    fromTime: pendingIntent.fromTime,
                    toTime: pendingIntent.toTime,
                });
                if (!isSuccessfulStatusCode(tripRes.statusCode)) throw new Error(tripRes.statusMessage || "Failed to create trip");
                const id = tripRes.data?.tripId ?? tripRes.data?.TripId ?? tripRes.data?.id ?? null;
                if (!id) throw new Error("Failed to retrieve trip ID from server");
                tripId = id;
            }
            
            createdTripId = tripId;

            // ── 2. Post preferences ─────────────────────────────────────────────
            if (tripId) {
                const tripPreferences = [
                    {
                        preferenceName: "Gender",
                        preferenceValue: pendingIntent.preferredGender ?? ""
                    },
                    {
                        preferenceName: "Languages",
                        preferenceValue: resolvedLanguageIds ?? ""
                    },
                    {
                        preferenceName: "Transportation",
                        preferenceValue: "true"
                    }
                ];
                
                // Removed isAdmin and added scheduleIds if available
                await post(`trip/preferences?tripId=${tripId}`, { tripPreferences }).catch(() => { });
            }     

            // ── 3. Select agent via canonical traveller flow ───────────────────
            const selectRes = await post("trip/schedule/select/agent", {
                tripId,
                agentId: agent.agentId,
                cityId: resolvedLocation.cityId,
                referenceId: currentReferenceId,
            });
            if (!isSuccessfulStatusCode(selectRes.statusCode)) throw new Error(selectRes.statusMessage || "Failed to select attendant");

            // Fetch confirmed booking details so we can show the exact platform fee charged.
            let platformFeeCharged: number | null = null;
            let platformFeeTaxAmount: number | null = null;
            let platformFeeTaxPercentage: number | null = null;
            let totalPlatformFeeCharged: number | null = null;
            try {
                const confirmDetailsRes = await get(`trip/confirm-booking?tripId=${tripId}`);
                if (!isSuccessfulStatusCode(confirmDetailsRes?.statusCode)) {
                    throw new Error(confirmDetailsRes?.statusMessage || "Unable to load platform fee details.");
                }
                const travellerTripInfo = confirmDetailsRes?.data?.travellerTripInfo;
                const rawFee = travellerTripInfo?.platformFee;
                const rawTaxAmount = travellerTripInfo?.taxAmount;
                const rawTaxPercentage = travellerTripInfo?.taxPercentage;
                const rawTotalPlatformFee = travellerTripInfo?.totalPlatformFee;
                const parsedFee = Number(rawFee);
                const parsedTaxAmount = Number(rawTaxAmount);
                const parsedTaxPercentage = Number(rawTaxPercentage);
                const parsedTotalPlatformFee = Number(rawTotalPlatformFee);
                platformFeeCharged = Number.isFinite(parsedFee) ? parsedFee : null;
                platformFeeTaxAmount = Number.isFinite(parsedTaxAmount) ? parsedTaxAmount : null;
                platformFeeTaxPercentage = Number.isFinite(parsedTaxPercentage) ? parsedTaxPercentage : null;
                totalPlatformFeeCharged = Number.isFinite(parsedTotalPlatformFee) ? parsedTotalPlatformFee : null;
            } catch (feeErr: any) {
                throw new Error(feeErr?.message || "Unable to load platform fee details.");
            }

            let fallbackEstimatedPlatformFee: number | null = null;
            if (resolvedLocation?.countryId) {
                fallbackEstimatedPlatformFee = await getEstimatedPlatformFee(
                    resolvedLocation.countryId,
                    pendingIntent.scheduleDate,
                    pendingIntent.fromTime,
                );
            }

            const amountToCharge = [totalPlatformFeeCharged, platformFeeCharged, fallbackEstimatedPlatformFee]
                .find((value) => typeof value === "number" && Number.isFinite(value) && value > 0) ?? 0;
            if (amountToCharge <= 0) {
                throw new Error("Invalid platform fee amount.");
            }

            if ((totalPlatformFeeCharged === null || totalPlatformFeeCharged <= 0) && amountToCharge > 0) {
                totalPlatformFeeCharged = amountToCharge;
            }

            if ((platformFeeCharged === null || platformFeeCharged <= 0) && amountToCharge > 0) {
                platformFeeCharged = amountToCharge;
            }

            const paymentRes = await post("trip-payment/stripe/intent/create", {
                tripId,
                scheduleId: null,
                paymentId: EMPTY_GUID,
                customerId: stripeCustomerId,
                tokenId,
                amount: amountToCharge,
                currency: "usd",
            });

            if (!isSuccessfulStatusCode(paymentRes.statusCode)) {
                throw new Error(paymentRes.statusMessage || "Failed to charge the platform fee.");
            }

            if (paymentRes.data?.paymentStatus && paymentRes.data.paymentStatus !== "succeeded") {
                throw new Error("Platform fee payment needs additional verification. Please complete payment from My Trips.");
            }

            setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === "assistant") {
                    updated[updated.length - 1] = {
                        ...last,
                        type: "success_card",
                        text: undefined,
                        booking: {
                            tripId,
                            agentName: agent.agentName,
                            scheduleDate: pendingIntent.scheduleDate,
                            fromTime: pendingIntent.fromTime,
                            hours: pendingIntent.hours,
                            totalPrice,
                            paymentStage: "platform_fee",
                            platformFeeCharged,
                            platformFeeTaxAmount,
                            platformFeeTaxPercentage,
                            totalPlatformFeeCharged,
                        },
                    };
                }
                return updated;
            });

            setBookingStep("booked");
            setPendingIntent(null);
            setSelectedAgent(null);
            setCurrentTripId(null);
            setCurrentReferenceId(null);
            setResolvedLocation(null);
            setResolvedLanguageIds(null);
            resetPaymentState();

            chatSessionRef.current?.sendMessage(
                `[SYSTEM: Booking confirmed. Agent: ${agent.agentName}. Estimated total trip amount: $${totalPrice}.]`
            ).catch(() => { });
        } catch (err: any) {
            const rawErrorMessage = err?.message || "Something went wrong.";
            const friendlyErrorMessage = getFriendlyBookingError(rawErrorMessage);
            let rolledBackTrip = false;

            if (createdTripId) {
                try {
                    await patch("trip/trip-delete", { tripId: createdTripId, scheduleId: null });
                    rolledBackTrip = true;
                    createdTripId = null;
                } catch {
                    rolledBackTrip = false;
                }
            }

            setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === "assistant") {
                    updated[updated.length - 1] = {
                        ...last,
                        type: "text",
                        text: rolledBackTrip
                            ? `We couldn't complete your booking because ${friendlyErrorMessage}. No trip was created. Please try again.`
                            : createdTripId
                                ? `Your trip was created, but payment setup still needs attention: ${friendlyErrorMessage}. Please complete payment from My Trips.`
                                : `We couldn't complete your booking because ${friendlyErrorMessage}. Please try again.`,
                    };
                }
                return updated;
            });
            setBookingStep("show_results");
            setSelectedAgent(null);
        }
    };

    const isBusy = isTyping || bookingStep === "searching" || bookingStep === "booking";

    const lastAssistantId = [...messages].reverse().find((m) => m.role === "assistant")?.id;

    return (
        <div
            style={{
                height: "calc(100vh - 112px)",
                display: "flex",
                flexDirection: "column",
                background: "#fff",
                borderRadius: 16,
                overflow: "hidden",
                boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
            }}
        >
            {/* Header */}
            <div
                style={{
                    background: "linear-gradient(135deg,#1a1a2e,#16213e)",
                    padding: "14px 24px",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    flexShrink: 0,
                }}
            >
                <div
                    style={{
                        background: "#6366f1",
                        borderRadius: "50%",
                        width: 42,
                        height: 42,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <RobotOutlined style={{ color: "#fff", fontSize: 20 }} />
                </div>
                <div>
                    <Text strong style={{ color: "#fff", fontSize: 18, display: "block" }}>
                        Trip Booking Assistant
                    </Text>
                    <Text style={{ color: "rgba(255,255,255,0.72)", fontSize: 14, lineHeight: 1.5 }}>
                        Powered by Gemini AI · Chat, ask questions, or book a trip
                    </Text>
                </div>
            </div>

            {/* Messages */}
            <div
                style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "20px 24px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                }}
            >
                {messages.map((msg, index) => {
                    const hasSuccessAfter = messages.slice(index + 1).some((m) => m.type === "success_card");
                    const disabledIntent = msg.type === "intent_card" && hasSuccessAfter;
                    return (
                        <MessageBubble
                            key={msg.id}
                            msg={msg}
                            isLastAssistant={msg.id === lastAssistantId}
                            isTyping={isBusy}
                            onConfirmIntent={handleConfirmIntent}
                            onRetryIntent={handleRetryIntent}
                            onSelectAgent={handleSelectAgent}
                            onConfirmBooking={handleFinalBook}
                            onBackToResults={handleBackToResults}
                            pendingIntent={pendingIntent}
                            bookingStep={bookingStep}
                            savedCardLast4={savedCardLast4}
                            useNewCard={useNewCard}
                            onUseSavedCard={handleUseSavedCard}
                            onUseNewCard={handleUseNewCard}
                            cardForm={cardForm}
                            onCardChange={handleCardFieldChange}
                            paymentMessage={paymentMessage}
                            paymentError={paymentError}
                            isStripeConfigured={Boolean(STRIPE_PUBLISHABLE_KEY)}
                            hasStripeCustomer={Boolean(stripeCustomerId)}
                            disabledIntent={disabledIntent}
                        />
                    );
                })}
                {isTyping && <TypingIndicator />}
                {(bookingStep === "searching" || bookingStep === "booking") && (
                    <TypingIndicator />
                )}
                <div ref={bottomRef} />
            </div>

            {/* Suggested starters — only shown before user sends first message */}
            {messages.length <= 1 && (
                <div
                    style={{
                        padding: "0 20px 12px",
                        display: "flex",
                        gap: 8,
                        flexWrap: "wrap",
                    }}
                >
                    {[
                        "What can you help me with?",
                        "Find me an attendant",
                        "What's fun to do when traveling?",
                        "How does TripAttendynt work?",
                    ].map((s) => (
                        <button
                            key={s}
                            onClick={() => {
                                setInput(s);
                                setTimeout(() => {
                                    (document.querySelector(".chat-input") as HTMLInputElement)?.focus();
                                }, 50);
                            }}
                            style={{
                                background: "#f0f0ff",
                                border: "1px solid #d0d0ff",
                                borderRadius: 20,
                                padding: "8px 14px",
                                fontSize: 14,
                                color: "#6366f1",
                                cursor: "pointer",
                                transition: "background 0.15s",
                            }}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            )}

            {/* Input bar */}
            <div
                style={{
                    padding: "12px 20px 16px",
                    borderTop: "1px solid #f0f0f0",
                    background: "#fff",
                    flexShrink: 0,
                }}
            >
                {bookingStep === "booked" ? (
                    <Button
                        block
                        size="large"
                        type="primary"
                        style={{ background: "#6366f1", border: "none", borderRadius: 12 }}
                        onClick={() => {
                            setBookingStep("idle");
                            addMessage({
                                role: "assistant",
                                type: "text",
                                text: "What else can I help you with? 😊",
                            });
                        }}
                    >
                        Continue chatting
                    </Button>
                ) : (
                    <div style={{ display: "flex", gap: 10 }}>
                        <Input
                            className="chat-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onPressEnter={handleSend}
                            placeholder="Ask me anything, or describe a trip you want to book…"
                            size="large"
                            style={{ borderRadius: 12, fontSize: 16, paddingTop: 6, paddingBottom: 6 }}
                            disabled={isBusy}
                        />
                        <Button
                            type="primary"
                            size="large"
                            icon={<SendOutlined />}
                            onClick={handleSend}
                            disabled={isBusy || !input.trim()}
                            style={{
                                background: "#6366f1",
                                border: "none",
                                borderRadius: 12,
                                minWidth: 48,
                                fontSize: 16,
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AiTripAgent;
